import { useEffect, useRef, useCallback } from 'react';
import { RelayNotificationService } from '../services/relayNotificationService';
import { GraphResolver } from '../services/graphResolver';
import { useIdentity } from './useIdentity';
import type { RelayHashEvent } from '../services/relayNotificationService';

/**
 * Hook for relay notifications.
 * Automatically processes new hashes when detected.
 */
export function useRelayNotifications(
  graphResolver: GraphResolver,
  autoProcess: boolean = true,
): {
  notificationService: RelayNotificationService | null;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
} {
  const { identity } = useIdentity();
  const serviceRef = useRef<RelayNotificationService | null>(null);

  useEffect(() => {
    if (graphResolver === null) {
      return;
    }

    serviceRef.current = new RelayNotificationService(graphResolver, identity ?? null);

    // Auto-process hashes when detected
    if (autoProcess) {
      const listener = async (event: RelayHashEvent): Promise<void> => {
        if (serviceRef.current === null) {
          return;
        }
        const objectType = event.objectType ?? 'unknown';
        const source = event.source ?? 'polling';
        console.info(
          `[RelayNotifications] New ${objectType} hash detected (${source}): ${event.hash.slice(0, 16)}... from ${event.relay}`,
        );
        // Process hash: fetch message, signatures, keys, decrypt and update graph
        // Use optimized processing based on object type if known
        const result =
          objectType !== 'unknown'
            ? await serviceRef.current.processHashByType(
                event.hash,
                objectType,
                {
                  fetchMessage: true,
                  fetchSignatures: true,
                  fetchKeys: true,
                  decryptAndUpdateGraph: true,
                },
              )
            : await serviceRef.current.processHash(event.hash, {
                fetchMessage: true,
                fetchSignatures: true,
                fetchKeys: true,
                decryptAndUpdateGraph: true,
              });
        if (result.graphUpdated) {
          console.info(
            `[RelayNotifications] Graph updated from ${objectType} hash ${event.hash.slice(0, 16)}...`,
          );
        } else if (result.error !== undefined) {
          console.warn(
            `[RelayNotifications] Error processing ${objectType} hash ${event.hash.slice(0, 16)}...: ${result.error}`,
          );
        }
      };

      serviceRef.current.addHashListener(listener);

      return () => {
        if (serviceRef.current !== null) {
          serviceRef.current.removeHashListener(listener);
        }
      };
    }
  }, [graphResolver, identity, autoProcess]);

  const startPolling = useCallback(
    (intervalMs: number = 60000): void => {
      if (serviceRef.current !== null) {
        serviceRef.current.startPolling(intervalMs);
      }
    },
    [],
  );

  const stopPolling = useCallback((): void => {
    if (serviceRef.current !== null) {
      serviceRef.current.stopPolling();
    }
  }, []);

  return {
    notificationService: serviceRef.current,
    startPolling,
    stopPolling,
  };
}
